import { AlertTriangle, Car, Trophy } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, LineChart } from "recharts";
import { monthLabel } from "@/lib/dpc";
import { TH } from "./StageCells";

// Test-drive fleet productivity — one metric, two decimals, no standard, no colour coding.
export const fmtTd = (v) => (v == null ? "—" : Number(v).toFixed(2));
export const TD_COL = "border-l-2 border-border/80";

export function NotSetFlag({ testId, className = "" }) {
  return <span className={`inline-flex items-center gap-1 rounded bg-amber-50 text-amber-800 border border-amber-200 px-1 py-0.5 text-[9px] font-semibold whitespace-nowrap ${className}`} data-testid={testId}>TD cars not set</span>;
}

// Fleet idle alert: below the group's TDs per car on each of the last 5 working days.
export function IdleFlag({ a, testId, className = "" }) {
  if (!a?.active) return null;
  const tip = `${a.text} (${a.from.slice(8)}–${a.to.slice(8)}): ${fmtTd(a.dealer_value)} vs group ${fmtTd(a.group_value)} TDs/car/day`;
  return <span className={`inline-flex items-center gap-0.5 rounded border border-foreground/30 bg-secondary px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider whitespace-nowrap ${className}`} title={tip} data-testid={testId}><AlertTriangle size={9} /> Idle {a.days.length}d</span>;
}

export function IdleTag({ testId }) {
  return <span className="ml-1.5 rounded border border-foreground/30 bg-secondary px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider" data-testid={testId}>Idle</span>;
}

export function TdHead({ testId }) {
  return <th className={`${TH} text-right ${TD_COL}`} rowSpan={2} title="Test drives ÷ TD cars ÷ working days (elapsed) · hover a value for the TD cars" data-testid={testId}>TDs/car/day</th>;
}

// Table cell: the figure with the TD cars on hover; amber flag when the dealer has no TD cars captured.
export function TdCell({ f, testId, bg = "", py = "py-1", agg = false }) {
  if (!f) return <td className={`px-2 ${py} text-right ${TD_COL} ${bg} text-muted-foreground`} data-testid={testId}>—</td>;
  if (f.needs_td_cars) return <td className={`px-2 ${py} text-right ${TD_COL} ${bg}`} data-testid={testId}><NotSetFlag /></td>;
  const tip = agg
    ? `${f.td_cars} TD cars · ${f.test_drives} test drives ÷ ${f.car_days} car-days (${f.dealers} dealers) · capacity-weighted`
    : `${f.td_cars} TD cars · ${f.test_drives} test drives ÷ ${f.car_days} car-days (${f.working_days} working days)`;
  return <td className={`px-2 ${py} text-right num font-semibold ${TD_COL} ${bg}`} title={tip} data-testid={testId}><span className="inline-flex items-center gap-1"><IdleFlag a={f.idle_alert} testId={testId ? `${testId}-idle` : undefined} />{fmtTd(f.per_car_day)}</span></td>;
}

function Stat({ label, value, sub, testId }) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="num text-lg font-bold leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Sparkline({ trend }) {
  const data = (trend || []).map((t) => ({ m: monthLabel(t.month).slice(0, 3), v: t.per_car_day, td: t.test_drives }));
  return (
    <div className="h-14 w-full" data-testid="td-fleet-sparkline">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 6, left: 6, bottom: 0 }}>
          <XAxis dataKey="m" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} interval={0} />
          <Tooltip formatter={(v, _n, p) => [`${fmtTd(v)} TDs/car/day · ${p.payload.td} TDs`, p.payload.m]} contentStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="v" stroke="#0f172a" strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Dealer Dashboard panel: TD cars, TDs / car / day for the period, rank, group best, group figure, 6-month trend, daily chart, model-wise table.
export function TdFleetPanel({ f, month }) {
  if (!f) return null;
  const daily = (f.daily || []).map((d) => ({ ...d, label: String(d.day) }));
  return (
    <div className="border bg-card rounded-md" data-testid="td-fleet-panel">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-sm font-semibold inline-flex items-center gap-1.5"><Car size={14} /> Test-drive fleet productivity <span className="text-muted-foreground font-normal text-xs">· {f.range?.[0]?.slice(8)}–{f.range?.[1]?.slice(8)} {monthLabel(month)}</span></h3>
        {f.needs_td_cars && <NotSetFlag testId="td-fleet-notset" className="text-[10px]" />}
        <IdleFlag a={f.idle_alert} testId="td-fleet-idle" className="text-[10px]" />
        <span className="ml-auto text-[10px] text-muted-foreground">{f.definition}</span>
      </div>
      {f.idle_alert?.active && (
        <div className="mx-4 mt-3 rounded-md border border-foreground/20 bg-secondary px-3 py-2 text-xs flex items-start gap-2" data-testid="td-fleet-idle-banner">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div><b>Fleet idle alert.</b> {f.idle_alert.text} ({f.idle_alert.from.slice(8)}–{f.idle_alert.to.slice(8)}): <b className="num">{fmtTd(f.idle_alert.dealer_value)}</b> vs group <b className="num">{fmtTd(f.idle_alert.group_value)}</b> TDs/car/day. Push test drives early — book demos against open enquiries today.</div>
        </div>
      )}
      <div className="p-4 grid gap-3 lg:grid-cols-5 [&>*]:min-w-0">
        <div className="lg:col-span-2 grid grid-cols-2 gap-2 content-start">
          <Stat label="TD cars" value={f.td_cars ?? "—"} sub={f.effective_from ? `since ${f.effective_from}` : "not captured"} testId="td-fleet-cars" />
          <Stat label="TDs / car / day" value={fmtTd(f.per_car_day)} sub={`${f.test_drives} TDs · ${f.working_days} working days`} testId="td-fleet-value" />
          <Stat label="Rank" value={f.rank ? <span className="inline-flex items-center gap-1"><Trophy size={14} className="text-amber-500" />{f.rank}<span className="text-xs text-muted-foreground font-normal">/ {f.of}</span></span> : "—"} sub="among dealers with TD cars" testId="td-fleet-rank" />
          <Stat label="Group best · group" value={<span>{fmtTd(f.group_best)} <span className="text-xs text-muted-foreground font-normal">· {fmtTd(f.group_avg)}</span></span>} sub={f.group_best_code ? `best ${f.group_best_code} · group capacity-weighted` : "—"} testId="td-fleet-group" />
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">6-month trend</div>
            <Sparkline trend={f.trend} />
          </div>
        </div>
        <div className="lg:col-span-3" data-testid="td-fleet-daily-chart">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Test drives per TD car — daily</div>
          <ResponsiveContainer width="100%" height={190}>
            <ComposedChart data={daily} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e5e9f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals />
              <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v, n) => [n === "TDs / car" ? fmtTd(v) : v, n]} labelFormatter={(l, p) => (p?.[0]?.payload?.date || l) + (p?.[0]?.payload?.working === false ? " · non-working day" : "")} />
              <Bar dataKey="test_drives" name="Test drives" fill="#cbd5e1" />
              <Line type="monotone" dataKey="per_car" name="TDs / car" stroke="#0f172a" strokeWidth={2} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      {f.model_rows?.length > 0 && (
        <div className="border-t overflow-auto" data-testid="td-fleet-models">
          <table className="w-full text-xs">
            <thead className="border-b"><tr>{["Model", "TD cars", "Test drives", "Car-days", "TDs / car / day"].map((h, i) => <th key={h} className={`${TH} ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
            <tbody>
              {f.idle_threshold != null && <tr><td colSpan={5} className="px-2 py-1 text-[10px] text-muted-foreground">Idle = model TDs/car/day at or below {f.idle_threshold_pct}% of the group figure ({fmtTd(f.idle_threshold)})</td></tr>}
              {f.model_rows.map((m) => (
                <tr key={m.model} className="border-b last:border-0 hover:bg-muted/50" data-testid={`td-fleet-model-${m.model.replace(/\s+/g, "_")}`}>
                  <td className="px-2 py-1 font-medium">{m.model}{m.idle && <IdleTag testId={`td-fleet-model-idle-${m.model.replace(/\s+/g, "_")}`} />}</td>
                  <td className="px-2 py-1 text-right num">{m.td_cars}</td>
                  <td className="px-2 py-1 text-right num">{m.test_drives}</td>
                  <td className="px-2 py-1 text-right num text-muted-foreground">{m.car_days}</td>
                  <td className="px-2 py-1 text-right num font-semibold">{fmtTd(m.per_car_day)}</td>
                </tr>
              ))}
              <tr className="bg-secondary/70 font-bold border-t">
                <td className="px-2 py-1">ALL TD CARS</td>
                <td className="px-2 py-1 text-right num">{f.td_cars}</td>
                <td className="px-2 py-1 text-right num">{f.test_drives}</td>
                <td className="px-2 py-1 text-right num">{f.car_days}</td>
                <td className="px-2 py-1 text-right num">{fmtTd(f.per_car_day)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
